import classnames from 'classnames';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Feedback } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

export interface IGroupedSelectOption<T extends string> {
    group: string;
    text: string;
    value: T;
}

interface IGroupedDropdownInputProps<T extends string> {
    disabled?: boolean;
    error?: string;
    notSelectedText?: string;
    onChange: (value: T | null) => void;
    onDoValidate?: (value: T | null) => void;
    selectOptions: IGroupedSelectOption<T>[];
    size?: '50' | 'small' | 'medium';
    sort?: boolean;
    value: T | null;
}

export function GroupedDropdownInput<T extends string>(props: FormContextProps<IGroupedDropdownInputProps<T>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => {
                const allOptions = [];
                if (properties.notSelectedText) {
                    allOptions.push(
                        <option
                            key={1}
                            value=""
                        >
                            {properties.notSelectedText}
                        </option>
                    );
                }

                if (properties.selectOptions.length > 0) {
                    const selectOptions = properties.selectOptions;

                    if (properties.sort) {
                        selectOptions.sort((a: IGroupedSelectOption<string>, b: IGroupedSelectOption<string>) => {
                            const valueA = a.group + a.text;
                            const valueB = b.group + b.text;
                            if (valueA > valueB) {
                                return 1;
                            } else if (valueA < valueB) {
                                return -1;
                            } else {
                                return 0;
                            }
                        });
                    }

                    let groupedOptions = [];
                    let currentOptgroup = selectOptions[0].group;
                    let index = 0;

                    for (const option of selectOptions) {
                        if (option.group !== currentOptgroup) {
                            allOptions.push(
                                <optgroup
                                    key={`${currentOptgroup}${index}`}
                                    label={currentOptgroup}
                                >
                                    {groupedOptions}
                                </optgroup>
                            );
                            index++;
                            groupedOptions = [];
                            currentOptgroup = option.group;
                        }
                        groupedOptions.push(
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.text}
                            </option>
                        );
                    }
                    allOptions.push(
                        <optgroup
                            key={currentOptgroup}
                            label={currentOptgroup}
                        >
                            {groupedOptions}
                        </optgroup>
                    );
                }

                const noSelection = !properties.value;
                const key = `input-${properties.size}`;
                const selectClassName = classnames('form-control form-dropdown', {
                    'form-control-danger': !!properties.error,
                    pristine: noSelection,
                    [key]: properties.size !== undefined,
                });

                return (
                    <>
                        <select
                            className={selectClassName}
                            disabled={properties.disabled}
                            onChange={async (e) => {
                                const value = e.currentTarget.value === '' ? null : (e.currentTarget.value as T);
                                await properties.onChange(value);
                                if (properties.onDoValidate) {
                                    await properties.onDoValidate(value);
                                }
                            }}
                            value={properties.value || ''}
                        >
                            {allOptions}
                        </select>
                        <Feedback
                            error={properties.error}
                            key="feedback"
                        />
                    </>
                );
            }}
        </FormContextConsumer>
    );
}

export function GroupedDropdownField<T extends string>(
    props: FormContextProps<IGroupedDropdownInputProps<T> & IFieldProps>
) {
    return (
        <Field {...props}>
            <GroupedDropdownInput<T> {...props} />
        </Field>
    );
}
